import React, {
  forwardRef,
  useEffect, useImperativeHandle, useMemo, useState,
} from 'react';
import { Grid, Typography } from '@mui/material';
import CardWrapper from 'components/Financial/CardWrapper';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import PondService from 'services/PondService';
import api from 'services/api';
import InputGroup from './InputGroup';
import { apportionmentSchema } from './schemas';
import { useLoadDataByLocation } from 'hooks/useLoadDataByLocation';

function Apportionment({
  billValue, callback = () => {},
}, ref) {
  const formik = useFormik({
    initialValues: {
      movements: [
        {
          accountCenter: null,
          ponds: [],
          value: null,
          type: 'Proporcional',
        },
      ],
    },
    validationSchema: apportionmentSchema,
  });

  const [ponds, setPonds] = useState([]);
  const [accountCenters, setAccountCenter] = useState([]);
  const [remainingTotalDisplayError, setRemainingTotalDisplayError] = useState('');

  const hasRemainingValue = useMemo(() => {
    const totalApportionment = formik.values.movements.reduce((acc, curr) => acc + curr.value, 0);
    return totalApportionment < billValue;
  }, [billValue, formik.values.movements]);

  function setData(data) {
    const movementsPayload = data.bill ? data.bill.movements : data.movements;
    const movements = movementsPayload?.length > 0 ? movementsPayload : formik.initialValues.movements;
    formik.setValues({ movements });
  }

  useLoadDataByLocation({ setData });

  useEffect(() => {
    async function loadPonds() {
      try {
        const pondList = await PondService.pondList();
        setPonds(pondList);
      } catch (error) {
        throw new Error();
      }
    }
    loadPonds();
  }, []);

  useEffect(() => {
    async function loadCostCenter() {
      try {
        const costCenter = (await api.get('/accounts-center')).data;
        setAccountCenter(costCenter);
      } catch (error) {
        throw new Error();
      }
    }
    loadCostCenter();
  }, []);

  useEffect(() => {
    if (!hasRemainingValue) {
      setRemainingTotalDisplayError('');
    }
  }, [hasRemainingValue]);

  const mappedPonds = useMemo(() => {
    const mappedPondsValues = ponds?.map((pond) => ({
      pond: { ...pond },
      value: 0,
    }));

    return mappedPondsValues;
  }, [ponds]);

  const hasMovements = formik.values.movements && formik.values.movements.length > 0;

  const handleSubmit = async () => {
    try {
      if (hasRemainingValue) {
        setRemainingTotalDisplayError('Soma dos valores é menor que o valor total da conta!');
      }
      await formik.submitForm();
    } catch (e) {}
  };

  useEffect(() => {
    callback({ isValid: formik.isValid && !hasRemainingValue, values: formik.values });
  }, [callback, formik.isValid, formik.values, hasRemainingValue]);

  useImperativeHandle(ref, () => ({
    handleSubmit,
  }));

  return (
    <FormikProvider value={formik}>
      <CardWrapper
        title="Rateio"
        tooltipMessage="Divida sua despesa em quantas categorias desejar. Também é possível dividir cada categoria entre seus viveiros."
      >
        <FieldArray name="movements">
          {({ remove, push }) => (
            <Grid item container rowGap={2}>
              <Grid item>
                <Typography color="red" width="100%">{remainingTotalDisplayError}</Typography>
              </Grid>
              {hasMovements && (
                formik.values.movements.map((_, index) => (
                  <InputGroup
                    key={index}
                    index={index}
                    billValue={billValue}
                    remainingTotalDisplayError={remainingTotalDisplayError}
                    accountCenters={accountCenters}
                    mappedPonds={mappedPonds}
                    onRemove={() => remove(index)}
                    onAdd={() => push({
                      accountCenter: null,
                      ponds: [],
                      value: null,
                      type: 'Proporcional',
                    })}
                  />
                ))
              )}
            </Grid>
          )}
        </FieldArray>
      </CardWrapper>
    </FormikProvider>
  );
}

export default forwardRef(Apportionment);
